import React from 'react';
import { BRAND } from '../config/region';
import { Send, Coins, GraduationCap, Wrench, Users, ShieldCheck, Zap, PenLine, ThumbsUp, Laptop, CheckCircle } from 'lucide-react';
import { t } from '../i18n';
import ParticleBackground from './ParticleBackground';
import SeoHead from './SeoHead';
import './AuthorsPage.css';

const AuthorsPage = () => (
  <div className="ap-wrap">
    <SeoHead
      title={t('Стать автором')}
      description={t('{0} — отличная возможность начать монетизировать своё творчество. Создавай увлекательные истории, в которые будут играть тысячи пользователей по всему миру.', BRAND)}
    />

    <ParticleBackground />

    <div className="ap-app">
      {/* Header */}
      <div className="ap-header">
        <h1 className="ap-title">{t('Стань автором интерактивных новелл с ИИ')}</h1>
        <p className="ap-subtitle">{t('{0} — отличная возможность начать монетизировать своё творчество. Создавай увлекательные истории, в которые будут играть тысячи пользователей по всему миру.', BRAND)}</p>
        <div className="ap-hero-btns">
          <a href="#contact" className="ap-btn-primary">{t('Начать создавать')}</a>
          <a href="#benefits" className="ap-btn-secondary">{t('Узнать больше')}</a>
        </div>
      </div>

      {/* Income */}
      <div className="ap-income">
        <p className="ap-income-title">{t('Твой творческий потенциал — твой доход')}</p>
        <p className="ap-income-text">{t('Каждая успешная новелла приносит автору')} <strong>{t('20% от дохода')}</strong> {t('и')} <strong>{t('20 000 звёзд')}</strong> {t('за каждую опубликованную новеллу. Чем популярнее твоя история — тем больше заработок.')}</p>
        <div className="ap-stats">
          <div className="ap-stat">
            <span className="ap-stat-value">20%</span>
            <span className="ap-stat-label">{t('от дохода')}</span>
          </div>
          <div className="ap-stat">
            <span className="ap-stat-value">20 000⭐</span>
            <span className="ap-stat-label">{t('за публикацию')}</span>
          </div>
          <div className="ap-stat">
            <span className="ap-stat-value">50+</span>
            <span className="ap-stat-label">{t('тыс. пользователей')}</span>
          </div>
          <div className="ap-stat">
            <span className="ap-stat-value">24/7</span>
            <span className="ap-stat-label">{t('поддержка')}</span>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <section id="benefits" className="ap-section">
        <h2 className="ap-section-title">{t('Преимущества')}</h2>
        <p className="ap-section-sub">{t('Почему авторы выбирают {0}', BRAND)}</p>
        <p className="ap-section-desc">{t('Мы создали все условия, чтобы ты мог полностью сосредоточиться на творчестве')}</p>
        <div className="ap-benefits">
          <div className="ap-benefit">
            <Coins size={18} className="ap-benefit-icon" />
            <span className="ap-benefit-name">{t('Выгодные условия')}</span>
            <span className="ap-benefit-text">{t('Получай 20% от дохода и 20 000 звёзд за каждую опубликованную новеллу. Чем популярнее твой контент — тем выше твой заработок без ограничений.')}</span>
          </div>
          <div className="ap-benefit">
            <GraduationCap size={18} className="ap-benefit-icon" />
            <span className="ap-benefit-name">{t('Обучение и поддержка')}</span>
            <span className="ap-benefit-text">{t('Расскажем, научим и поможем разобраться с конструктором. Персональное сопровождение до выхода твоей первой новеллы.')}</span>
          </div>
          <div className="ap-benefit">
            <Wrench size={18} className="ap-benefit-icon" />
            <span className="ap-benefit-name">{t('Современные инструменты')}</span>
            <span className="ap-benefit-text">{t('Предоставим доступ к удобному конструктору и всем необходимым сервисам для создания интерактивных новелл.')}</span>
          </div>
          <div className="ap-benefit">
            <Users size={18} className="ap-benefit-icon" />
            <span className="ap-benefit-name">{t('Творческое сообщество')}</span>
            <span className="ap-benefit-text">{t('Присоединяйся к сообществу авторов {0}. Обменивайся опытом, получай обратную связь и вдохновляйся работами коллег.', BRAND)}</span>
          </div>
          <div className="ap-benefit">
            <ShieldCheck size={18} className="ap-benefit-icon" />
            <span className="ap-benefit-name">{t('Прозрачные правила')}</span>
            <span className="ap-benefit-text">{t('Чёткие и понятные условия сотрудничества. Никаких скрытых комиссий — ты всегда знаешь, сколько зарабатываешь.')}</span>
          </div>
          <div className="ap-benefit">
            <Zap size={18} className="ap-benefit-icon" />
            <span className="ap-benefit-name">{t('Быстрый старт')}</span>
            <span className="ap-benefit-text">{t('Уже через пару недель ты можешь опубликовать свою первую новеллу. Мы сопровождаем автора на каждом этапе.')}</span>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="ap-section">
        <h2 className="ap-section-title">{t('Требования')}</h2>
        <p className="ap-section-sub">{t('Кого мы ищем')}</p>
        <p className="ap-section-desc">{t('Мы ценим творческих людей, готовых учиться и развиваться')}</p>
        <div className="ap-requirements">
          <div className="ap-req">
            <PenLine size={18} className="ap-req-icon" />
            <span className="ap-req-name">{t('Грамотность')}</span>
            <span className="ap-req-text">{t('Умение писать грамотно и складно — основа любой истории. Мы ценим чистый язык и выразительный стиль.')}</span>
          </div>
          <div className="ap-req">
            <ThumbsUp size={18} className="ap-req-icon" />
            <span className="ap-req-name">{t('Адекватность')}</span>
            <span className="ap-req-text">{t('Умение работать в команде, принимать обратную связь и конструктивно общаться — ключ к успешному сотрудничеству.')}</span>
          </div>
          <div className="ap-req">
            <Laptop size={18} className="ap-req-icon" />
            <span className="ap-req-name">{t('Желание учиться')}</span>
            <span className="ap-req-text">{t('Готовность разобраться с конструктором и освоить новые инструменты. ПК или ноутбук обязателен для работы.')}</span>
          </div>
        </div>
      </section>

      {/* Help */}
      <div className="ap-help">
        <p className="ap-help-title">{t('Мы поможем!')}</p>
        <p className="ap-help-sub">{t('Полная поддержка на протяжении всего времени')}</p>
        <ul className="ap-help-list">
          <li><CheckCircle size={14} className="ap-help-icon" />{t('Расскажем и покажем, как работает конструктор')}</li>
          <li><CheckCircle size={14} className="ap-help-icon" />{t('Научим создавать интерактивные ветвления')}</li>
          <li><CheckCircle size={14} className="ap-help-icon" />{t('Предоставим доступ ко всем сервисам')}</li>
          <li><CheckCircle size={14} className="ap-help-icon" />{t('Сопроводим до выхода новеллы')}</li>
          <li><CheckCircle size={14} className="ap-help-icon" />{t('Поддержим на каждом этапе')}</li>
        </ul>
      </div>

      {/* Contact */}
      <div id="contact" className="ap-contact">
        <h2 className="ap-contact-title">{t('Готов начать свой путь автора?')}</h2>
        <p className="ap-contact-sub">{t('Напиши нам — ответим в течение 24 часов')}</p>
        <div className="ap-contact-btns">
          <a href="https://forms.yandex.ru/cloud/6a36840cd046882832495672/" target="_blank" rel="noopener noreferrer" className="ap-contact-btn">
            <Send size={16} />
            {t('Заполнить форму')}
          </a>
        </div>
      </div>
    </div>
  </div>
);

export default AuthorsPage;
